// src/pages/analytics/DashboardViewPage.tsx
// /analytics/:dashboardId — dashboard view: widget grid, add/edit/delete
// widgets, rename/describe, share, duplicate, delete. GetDashboard, GetWidget
// (per placement), UpdateDashboard, ShareDashboard, DuplicateDashboard,
// DeleteDashboard, CreateWidget/UpdateWidget/DeleteWidget (via dialogs/cards).

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { analyticsApi } from '@core/api';
import type { AvailableDataSource, Dashboard, Widget } from '@core/types/analytics';
import { useScope } from '@/hooks/useScope';
import { DashboardFormDialog, ShareDashboardDialog, WidgetCard, WidgetDialog, errMessage } from '@/features/analytics';

export function DashboardViewPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const { dashboardId } = useParams<{ dashboardId: string }>();

  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [dataSources, setDataSources] = useState<AvailableDataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [widgetDialog, setWidgetDialog] = useState<{ open: boolean; widgetId?: string }>({ open: false });

  const load = () => {
    if (!dashboardId) return;
    setLoading(true);
    setError(null);
    analyticsApi
      .GetDashboard(dashboardId)
      .then(async (res) => {
        setDashboard(res.dashboard ?? null);
        const ids = (res.dashboard?.layout ?? []).map((p) => p.widget_id).filter(Boolean);
        const loaded = await Promise.all(
          ids.map((id) =>
            analyticsApi
              .GetWidget(id)
              .then((r) => r.widget)
              .catch(() => undefined),
          ),
        );
        setWidgets(loaded.filter((w): w is Widget => !!w));
      })
      .catch((e: unknown) => setError(errMessage(e, t('analytics.dashboards.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(load, [dashboardId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    analyticsApi.ListAvailableDataSources().then((res) => setDataSources(res.data_sources ?? [])).catch(() => undefined);
  }, []);

  const duplicate = async () => {
    if (!dashboard?.entity?.id) return;
    try {
      const res = await analyticsApi.DuplicateDashboard(dashboard.entity.id, {});
      if (res.dashboard?.entity?.id) navigate(`/analytics/${res.dashboard.entity.id}`);
    } catch (e) {
      setError(errMessage(e, t('analytics.dashboards.duplicateError')));
    }
  };

  const handleDelete = async () => {
    if (!dashboard?.entity?.id) return;
    setDeleting(true);
    try {
      await analyticsApi.DeleteDashboard(dashboard.entity.id);
      navigate('/analytics');
    } catch (e) {
      setError(errMessage(e, t('analytics.dashboards.deleteError')));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 p-6" aria-busy="true">
        <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="h-40 animate-pulse rounded-md bg-muted" />
          <div className="h-40 animate-pulse rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-6">
        <Alert variant="error">{error ?? t('analytics.dashboards.notFound')}</Alert>
        <Link to="/analytics" className="mt-3 inline-block text-sm text-primary hover:underline">
          {t('analytics.backToDashboards')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Link to="/analytics" className="text-xs text-primary hover:underline">
            {t('analytics.backToDashboards')}
          </Link>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">{dashboard.name}</h1>
            <Badge variant="secondary">{t(`analytics.visibility.${dashboard.visibility}`)}</Badge>
          </div>
          {dashboard.description && <p className="text-sm text-muted-foreground">{dashboard.description}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditOpen(true)}>
            {t('common.edit')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setShareOpen(true)}>
            {t('analytics.dashboards.share')}
          </Button>
          <Button size="sm" variant="secondary" onClick={duplicate}>
            {t('common.duplicate')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
            {t('common.delete')}
          </Button>
          <Button size="sm" onClick={() => setWidgetDialog({ open: true })}>
            {t('analytics.widgets.create')}
          </Button>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {widgets.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('analytics.widgets.empty')}</p>
          <Button size="sm" className="mt-3" onClick={() => setWidgetDialog({ open: true })}>
            {t('analytics.widgets.create')}
          </Button>
        </div>
      )}

      {widgets.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {widgets.map((w) => (
            <div key={w.entity?.id} className="min-h-[220px]">
              <WidgetCard
                widget={w}
                scope={scope}
                onEdit={() => setWidgetDialog({ open: true, widgetId: w.entity?.id })}
                onDeleted={load}
              />
            </div>
          ))}
        </div>
      )}

      <DashboardFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        scope={scope}
        dashboard={dashboard}
        onSaved={(d) => setDashboard(d)}
      />
      <ShareDashboardDialog open={shareOpen} onClose={() => setShareOpen(false)} dashboard={dashboard} onSaved={(d) => setDashboard(d)} />
      {dashboardId && (
        <WidgetDialog
          open={widgetDialog.open}
          onClose={() => setWidgetDialog({ open: false })}
          dashboardId={dashboardId}
          scope={scope}
          dataSources={dataSources}
          widgetId={widgetDialog.widgetId}
          onSaved={load}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('analytics.dashboards.deleteConfirmTitle')}
        description={t('analytics.dashboards.deleteConfirmBody', { name: dashboard.name })}
        destructive
        isConfirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
